import React from 'react';
import { Dialog, DialogContent, DialogActions, Button } from '@mui/material';
import { HelpCircle, CheckCircle2, AlertCircle, RefreshCw } from 'lucide-react';

export const APPROVAL_STATUS = {
  IDLE: 'IDLE',
  GENERATING_PAYROLL: 'GENERATING_PAYROLL',
  GENERATING_BANK_REPORT: 'GENERATING_BANK_REPORT',
  PROCESSING_BATCHES: 'PROCESSING_BATCHES',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
};

const STEP_LABELS = [
  { id: APPROVAL_STATUS.GENERATING_PAYROLL, label: 'Calculating and Submitting Payroll' },
  { id: APPROVAL_STATUS.GENERATING_BANK_REPORT, label: 'Generating Bank Transfer Report' },
  { id: APPROVAL_STATUS.PROCESSING_BATCHES, label: 'Clearing Batches' },
];

export default function PayrollApprovalProgressDialog({
  open,
  status,
  errorMessage,
  onConfirm,
  onClose,
}) {
  const isProcessing =
    status === APPROVAL_STATUS.GENERATING_PAYROLL ||
    status === APPROVAL_STATUS.GENERATING_BANK_REPORT || 
    status === APPROVAL_STATUS.PROCESSING_BATCHES;

  const isCompleted = status === APPROVAL_STATUS.COMPLETED;
  const isFailed = status === APPROVAL_STATUS.FAILED;

  // Compute progress percentage
  const progressPercent =
    status === APPROVAL_STATUS.GENERATING_PAYROLL ? 33 :
    status === APPROVAL_STATUS.GENERATING_BANK_REPORT ? 66 :
    status === APPROVAL_STATUS.PROCESSING_BATCHES ? 85 :
    status === APPROVAL_STATUS.COMPLETED ? 100 : 0;

  return (
    <Dialog
      open={!!open}
      onClose={isProcessing ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ className: '!rounded-xl !shadow-lg' }}
    >
      <DialogContent className="!px-5 !pt-5 !pb-4">
        {status === APPROVAL_STATUS.IDLE && (
          <div className="flex items-start gap-3.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
              <HelpCircle size={18} strokeWidth={2} />
            </div>
            <div className="min-w-0 pt-0.5">
              <h2 className="text-[14px] font-semibold text-gray-900">
                Confirm Payroll Submission
              </h2>
              <p className="mt-1.5 text-[13px] leading-5 text-gray-500">
                Are you sure you want to submit this payroll run for approval? This will calculate the payroll and automatically generate the corresponding bank transfer report.
              </p>
            </div>
          </div>
        )}

        {(isProcessing || isCompleted || isFailed) && (
          <div className="space-y-4 pt-1">
            <div className="flex items-center gap-3">
              {isCompleted ? (
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                  <CheckCircle2 size={18} />
                </div>
              ) : isFailed ? (
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600">
                  <AlertCircle size={18} />
                </div>
              ) : (
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <RefreshCw size={18} className="animate-spin" />
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {isCompleted
                    ? 'Processing Complete'
                    : isFailed
                    ? 'Processing Failed'
                    : 'Processing Submission...'}
                </h3>
                <p className="text-xs text-gray-500">
                  {isCompleted
                    ? 'Payroll and bank reports were generated successfully.'
                    : isFailed
                    ? errorMessage || 'An error occurred during execution.'
                    : 'Please wait while we complete the requested operations.'}
                </p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="space-y-1.5">
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                <div
                  className={`h-full transition-all duration-500 ${
                    isFailed ? 'bg-red-500' : isCompleted ? 'bg-emerald-500' : 'bg-primary-600'
                  }`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="flex justify-between text-[11px] font-medium text-gray-500">
                <span>Progress</span>
                <span>{progressPercent}%</span>
              </div>
            </div>

            {/* Step Checklists */}
            <div className="space-y-2 rounded-lg border border-gray-100 bg-gray-50/70 p-3">
              {STEP_LABELS.map((step, idx) => {
                const isStepDone =
                  isCompleted ||
                  (idx === 0 && status === APPROVAL_STATUS.GENERATING_BANK_REPORT || status === APPROVAL_STATUS.PROCESSING_BATCHES) || 
                  (idx === 1 && status === APPROVAL_STATUS.PROCESSING_BATCHES);

                const isStepActive = status === step.id;

                return (
                  <div key={step.id} className="flex items-center gap-2 text-xs">
                    {isStepDone ? (
                      <CheckCircle2 size={14} className="text-emerald-600" />
                    ) : isStepActive ? (
                      <RefreshCw size={14} className="animate-spin text-primary-600" />
                    ) : (
                      <div className="h-3.5 w-3.5 rounded-full border border-gray-300" />
                    )}
                    <span
                      className={
                        isStepDone
                          ? 'text-gray-900 font-medium'
                          : isStepActive
                          ? 'text-primary-700 font-medium'
                          : 'text-gray-400'
                      }
                    >
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions className="!border-t !border-gray-100 !px-5 !py-3 !gap-1">
        {status === APPROVAL_STATUS.IDLE ? (
          <>
            <Button
              type="button"
              onClick={onClose}
              className="!h-8 !rounded-lg !px-3 !text-[12px] !font-semibold !normal-case !text-gray-500 hover:!bg-gray-50 hover:!text-gray-700"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={onConfirm}
              className="!h-8 !rounded-lg !px-3.5 !text-[12px] !font-semibold !normal-case !shadow-none !bg-primary-600 hover:!bg-primary-700"
            >
              Confirm & Submit
            </Button>
          </>
        ) : (
          <Button
            type="button"
            variant={isCompleted ? 'contained' : 'outlined'}
            disabled={isProcessing}
            onClick={onClose}
            className={`!h-8 !rounded-lg !px-3.5 !text-[12px] !font-semibold !normal-case ${
              isCompleted
                ? '!bg-primary-600 !text-white'
                : '!text-gray-600 !border-gray-300'
            }`}
          >
            {isProcessing ? 'Please wait...' : 'Close'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}